import React, { useState, useEffect } from "react";
import { Card, Button, Modal, Form, Row, Col } from "react-bootstrap";
import { Formik, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import FormikSelectField from "../../../../shared/components/FormikSelectField";

const WorkflowDepartments = ({
  values,
  setFieldValue,
  adminGroups,
  employees,
  loadEmployeesByGroup,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const [localDepartment, setLocalDepartment] = useState(null);
  const [localAssignedTo, setLocalAssignedTo] = useState(null);

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  // Assign correct sequence after every update
  // const reorderDepartments = (list) =>
  //   list
  //     .map((item, index) => ({ ...item, order: index + 1 }))
  //     .sort((a, b) => a.order - b.order);

  const reorderDepartments = (list) => {
    const active = list.filter((d) => d.status === true);
    const inactive = list.filter((d) => d.status === false);

    const orderedActive = active.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    const orderedInactive = inactive.map((item, index) => ({
      ...item,
      order: orderedActive.length + index + 1,
    }));

    return [...orderedActive, ...orderedInactive];
  };


  // Drag end handler
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    // if (activeFilter !== "all") return;
    setExpandedIndex(null);
    const items = Array.from(values.childDepartments || []);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const reOrdered = reorderDepartments(items);
    setFieldValue("childDepartments", reOrdered);
  };

  const openAdd = () => {
    setEditIndex(null);
    setLocalDepartment(null);
    setLocalAssignedTo(null);
    setShowModal(true);
  };

  const openEdit = (index) => {
    setEditIndex(index);
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal) return;

    if (editIndex !== null) {
      const dept = values.childDepartments?.[editIndex];

      if (dept?.departmentId) {
        setLocalDepartment(dept.departmentId);
        loadEmployeesByGroup(dept.departmentId);
      }

      if (dept?.assignedToId) {
        setLocalAssignedTo(dept.assignedToId);
      }
    } else {
      setLocalDepartment(null);
      setLocalAssignedTo(null);
    }
  }, [showModal, editIndex]);

const deleteChild = (index) => {
  const list = [...values.childDepartments];
  const item = list[index];

  // New item → remove
  if (!item?.id) {
    list.splice(index, 1);
  } else {
    // Existing → soft delete
    list[index] = {
      ...item,
      status: false,
    };
  }

  setFieldValue("childDepartments", reorderDepartments(list));
};


  const initialModalValues =
    editIndex !== null
      ? values.childDepartments[editIndex]
      : {
          departmentId: "",
          departmentName: "",
          assignedToId: "",
          assignedToName: "",
          instructions: "",
          status: true,
        };

  const modalValidationSchema = Yup.object({
    departmentId: Yup.string().required("Department is required"),
    instructions: Yup.string().required("Instructions are required"),
  });

  const getFilteredDepartments = () => {
    const all = [...(values.childDepartments || [])].sort(
      (a, b) => a.order - b.order
    );

    if (activeFilter === "active") {
      return all.filter((d) => d.status === true);
    }

    if (activeFilter === "inactive") {
      return all.filter((d) => d.status === false);
    }

    return all;
  };

const getNextOrder = (status) => {
  const list = values.childDepartments || [];

  const activeCount = list.filter((d) => d.status === true).length;
  const inactiveCount = list.filter((d) => d.status === false).length;

  if (status === true) {
    return activeCount + 1;
  }

  return activeCount + inactiveCount + 1;
};

 

  return (
    <Card className="mb-4">
      <Card.Header>Child Department Tickets</Card.Header>

      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button onClick={openAdd} className="default-btn-color mb-3">
            + Add Child Department
          </Button>

          {/* Active/Inactive Filter Toggle Buttons */}
          <div className="btn-group" role="group" aria-label="Question filter">
            <button
              type="button"
              className={`btn btn-sm ${
                activeFilter === "all" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setActiveFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeFilter === "active"
                  ? "btn-success"
                  : "btn-outline-success"
              }`}
              onClick={() => setActiveFilter("active")}
            >
              Active
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeFilter === "inactive"
                  ? "btn-danger"
                  : "btn-outline-danger"
              }`}
              onClick={() => setActiveFilter("inactive")}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* DRAGGABLE LIST */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId="childDepartments"
            // isDragDisabled={activeFilter !== "all"}
          >
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {getFilteredDepartments().map((dept, index) => (
                  <Draggable
                    key={`dept-${index}`}
                    draggableId={`dept-${index}`}
                    index={index}
                    isDragDisabled={!dept.status}
                  >
                    {(drag) => (
                      <Card
                        className="mb-2 p-3"
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        {...drag.dragHandleProps}
                        style={{
                          ...drag.draggableProps.style,
                          cursor: dept.status ? "pointer" : "not-allowed",
                          opacity: dept.status ? 1 : 0.7,
                          userSelect: "none",
                        }}
                        onClick={() =>
                          setExpandedIndex(
                            expandedIndex === index ? null : index
                          )
                        }
                      >
                        {/* HEADER ROW */}
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-bold">
                            {dept.order}. {dept.departmentName}
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="badge"
                              style={{
                                backgroundColor: dept.status
                                  ? "rgba(25, 135, 84, 0.15)"
                                  : "rgba(108, 117, 125, 0.15)",
                                color: dept.status ? "#198754" : "#6c757d",
                              }}
                            >
                              {dept.status ? "Active" : "Inactive"}
                            </span>

                            <i
                              className="fas fa-edit"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(index);
                              }}
                            />

                            <i
                              className="fas fa-trash"
                              style={{
                                cursor: "pointer",
                                color: "red",
                                opacity: dept.status ? 1 : 0,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteChild(index);
                              }}
                              title={
                                dept.status
                                  ? "Delete Department"
                                  : "Delete disabled for inactive departments"
                              }
                            />
                          </div>
                        </div>

                        {/* EXPANDED DETAILS */}
                        {expandedIndex === index && (
                          <div className="mt-3">
                            {dept.assignedToName && (
                              <div className="mb-1">
                                <strong>Assigned To:</strong>{" "}
                                {dept.assignedToName}
                              </div>
                            )}

                            {dept.instructions && (
                              <div>
                                <strong>Instructions:</strong>
                                <pre className="p-2 bg-light rounded mt-1">
                                  {dept.instructions}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* MODAL */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          backdrop="static"
          centered
          size="lg"
        >
          <Formik
            enableReinitialize
            initialValues={initialModalValues}
            validationSchema={modalValidationSchema}
            onSubmit={(modalValues) => {
              const updated = [...(values.childDepartments || [])];

              if (editIndex !== null) {
                updated[editIndex] = modalValues;
              } else {
                updated.push(modalValues);
              }

              setFieldValue("childDepartments", reorderDepartments(updated));
              setShowModal(false);
            }}
          >
            {({ values: modalValues, setFieldValue, submitCount }) => {
              const onDepartmentChange = (value) => {
                setLocalDepartment(value);
                setLocalAssignedTo(null);

                const group = adminGroups.find(
                  (g) => String(g.group_id) === String(value)
                );

                setFieldValue("departmentId", value);
                setFieldValue("departmentName", group?.group_name || "");
                setFieldValue("assignedToId", "");
                setFieldValue("assignedToName", "");

                loadEmployeesByGroup(value);
              };

              const onAssignedToChange = (value) => {
                setLocalAssignedTo(value);

                const emp = employees.find(
                  (e) => String(e.id) === String(value)
                );

                setFieldValue("assignedToId", value);
                setFieldValue(
                  "assignedToName",
                  emp ? `${emp.first_name} ${emp.last_name}` : ""
                );
              };

              return (
                <FormikForm>
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {editIndex !== null
                        ? "Edit Child Department Ticket"
                        : "Add Child Department Ticket"}
                    </Modal.Title>
                  </Modal.Header>

                  <Modal.Body>
                    {/* Department */}
                    <Form.Group className="mb-3">
                      <label className="form-label">
                        Department <span className="error-text">*</span>
                      </label>

                      <FormikSelectField
                        name="departmentId"
                        value={localDepartment || ""}
                        onChange={(name, value) => onDepartmentChange(value)}
                        placeholder="Select department"
                      >
                        {adminGroups.filter(
                            (g) =>
                              g.group_name !== "All Administrator" &&
                              g.group_name !== "All Users"
                          ).map((g) => (
                          <option key={g.group_id} value={g.group_id}>
                            {g.group_name}
                          </option>
                        ))}
                      </FormikSelectField>

                      {submitCount > 0 && (
                        <ErrorMessage
                          name="departmentId"
                          component="div"
                          className="error-text"
                        />
                      )}
                    </Form.Group>

                    {/* Assigned To */}
                    <Form.Group className="mb-3">
                      <label className="form-label">Default Assigned To</label>

                      <FormikSelectField
                        name="assignedToId"
                        value={localAssignedTo || ""}
                        onChange={(name, value) => onAssignedToChange(value)}
                        placeholder="Select employee"
                        disabled={!localDepartment}
                      >
                        <option value="">Select employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name}
                          </option>
                        ))}
                      </FormikSelectField>
                    </Form.Group>

                    {/* Instructions */}
                    <Form.Group className="mb-3">
                      <label className="form-label">
                        Instructions <span className="error-text">*</span>
                      </label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={modalValues.instructions}
                        onChange={(e) =>
                          setFieldValue("instructions", e.target.value)
                        }
                      />
                      {submitCount > 0 && (
                        <ErrorMessage
                          name="instructions"
                          component="div"
                          className="error-text"
                        />
                      )}
                    </Form.Group>

                    {/* Status */}
                    <Row>
                      <Col md={6}>
                        <label className="form-label">Status</label>
                        <Form.Select
                          value={modalValues.status ? "true" : "false"}
                          disabled={!modalValues.id}
                          onChange={(e) => {
                            const isActive = e.target.value === "true";
                            setFieldValue("status", isActive);
                          }}
                        >
                          <option value="true">Active</option>
                          <option value="false" disabled>
                            Inactive
                          </option>
                        </Form.Select>
                      </Col>

                      <Col md={6}>
                        <label className="form-label">Order</label>
                        <Form.Control
                          value={
                            editIndex !== null
                              ? values.childDepartments[editIndex]?.order
                              : getNextOrder(modalValues.status)
                          }
                          disabled
                          style={{
                            height: "38px",
                            padding: "0.375rem 0.75rem",
                          }}
                        />
                      </Col>
                    </Row>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button className="default-btn-color" type="submit">
                      Save Child Department
                    </Button>
                  </Modal.Footer>
                </FormikForm>
              );
            }}
          </Formik>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default WorkflowDepartments;
